import { type FC, useRef, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";
import ModalWithSteps from "../../modal/ModalWithSteps";
import ConfirmLicensingStep from "../commonSteps/confirmLicensingStep/ConfirmLicensingStep";
import { Button } from "components/buttons/button/Button";
import "./peopleLicenseModal.scss";
import { PacksContextStep } from "../commonSteps";
import { Strings } from "enums";
import { type RenderModalActionsParams } from "../commonSteps/packsContextStep/PacksContextStep";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { bindAction } from "interfaces/redux";
import { resetContentPacksAction } from "features/Licensing/ContentAssignmentModalLicensingSteps/state/thunks/assignmentModalLicensingThunk";
import { type PackOverview } from "features/Licensing/Packs/types/state";
import PaymentModal from "../../../features/Licensing/Packs/PaymentModal/PaymentModal";
import useToggle from "../../../hooks/useToggle";
import { FeatureFlags } from "../../../featureFlags";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import { type LicenseConfirmationFormRef } from "../../../features/Licensing/ContentAssignmentModalLicensingSteps/LicenseConfirmationModalContent/LicenseConfirmationBlockV2/LicenseConfirmationBlock";
import { updateLicensePackInfo } from "../../../features/Licensing/ContentAssignmentModalLicensingSteps/state/slices/licenseConfirmationInfoSlice";

interface ConfirmLicensingRenderModalActionsParams extends RenderModalActionsParams {
  hasPreviousStep: boolean;
}

export interface Props {
  userIds?: number[];
  groupIds?: number[];
  packs?: PackOverview[];
  show: boolean;
  onCancel(): void;
  onConfirm(): void;
  onIssueLicensePacksChange: (ids: number[]) => void;
}

export type AllProps = PropsFromRedux & Props;

export const AddPeoplePacksIssueLicenseModal: FC<AllProps> = ({
  userIds,
  groupIds,
  packs,
  show,
  onCancel,
  onConfirm,
  onIssueLicensePacksChange,
  resetContentPacks,
  updateLicensePackInfo,
  isPurchaseNeeded,
  paymentPacks,
}) => {
  const [isDataValid, setIsDataValid] = useState(false);
  const [skipPacksContextStep, setSkipPacksContextStep] = useState(false);
  const [packsContextStepHeader, setPacksContextStepHeader] = useState<string>();
  const [showPaymentModal, togglePaymentModal] = useToggle(false);
  const additionalPurchaseEnabled = useFeatureFlag(FeatureFlags.AdditionalPurchase);
  const licenseConfirmationFormRef = useRef<LicenseConfirmationFormRef>({ getValues: () => [] });

  const getPacksContextItems = () =>
    packs?.map((x) => {
      return { type: "pack", id: x.id.toString(), title: x.title, thumbnailUrl: x.thumbnailImageUrl };
    });

  const packContextStepModalActionsParams: RenderModalActionsParams = {
    isDataValid,
    onIsDataValidChange: setIsDataValid,
  };

  const confirmLicensingStepModalActionsParams: ConfirmLicensingRenderModalActionsParams = {
    isDataValid,
    onIsDataValidChange: setIsDataValid,
    hasPreviousStep: !skipPacksContextStep,
  };

  const onClose = () => {
    setSkipPacksContextStep(false);
    resetContentPacks();
  };

  const getRenderModalActionsPacksContextStepFunction = (params: RenderModalActionsParams) => {
    return (nextStep: () => void) => {
      return (closeModal: Function) => {
        const { isDataValid, onIsDataValidChange } = params;
        return (
          <>
            <Button
              basic
              className="cancel"
              content="Cancel"
              onClick={() => {
                onCancel();
                closeModal();
              }}
            />
            <Button
              primary
              className="next"
              content="Next"
              onClick={() => {
                onIsDataValidChange(false);
                nextStep();
              }}
              disabled={!isDataValid}
            />
          </>
        );
      };
    };
  };

  const getRenderModalActionsConfirmLicensingStepFunction = (params: ConfirmLicensingRenderModalActionsParams) => {
    return (_: () => void, prevStep: () => void) => {
      return (closeModal: Function) => {
        const { isDataValid, onIsDataValidChange, hasPreviousStep } = params;

        const confirmHandler = () => {
          if (additionalPurchaseEnabled && isPurchaseNeeded) {
            updateLicensePackInfo(licenseConfirmationFormRef.current.getValues());
            togglePaymentModal();
          } else {
            onConfirm();
          }
          closeModal();
        };
        return (
          <>
            <Button
              primary
              className={hasPreviousStep ? "previous" : "cancel"}
              content={hasPreviousStep ? "Previous" : "Cancel"}
              onClick={() => {
                if (hasPreviousStep) {
                  onIsDataValidChange(true);
                  prevStep();
                } else {
                  onCancel();
                  closeModal();
                }
              }}
            />
            <Button
              primary
              className="confirm"
              content={additionalPurchaseEnabled && isPurchaseNeeded ? "Proceed to Payment" : "Confirm"}
              onClick={confirmHandler}
              disabled={!isDataValid}
            />
          </>
        );
      };
    };
  };

  const onPaymentSuccess = () => {
    togglePaymentModal();
    onConfirm();
    onClose();
  };

  const onClosePaymentModal = () => {
    togglePaymentModal();
    onClose();
  };

  return (
    <>
      <ModalWithSteps
        className="people-license-modal"
        scrolling
        showModal={show}
        onCancel={onCancel}
        onBeforeClose={onClose}
      >
        {!skipPacksContextStep && (
          <PacksContextStep
            header={packsContextStepHeader}
            renderModalActions={getRenderModalActionsPacksContextStepFunction(packContextStepModalActionsParams)}
            info={getPacksContextItems() ?? []}
            onIsDataValidChange={setIsDataValid}
            noDataLoaded={() => setSkipPacksContextStep(true)}
            setModalStepHeader={() => setPacksContextStepHeader(Strings.modalTitles.licensing)}
          />
        )}
        <ConfirmLicensingStep
          onIsDataValidChange={setIsDataValid}
          header="License Confirmation"
          renderModalActions={getRenderModalActionsConfirmLicensingStepFunction(confirmLicensingStepModalActionsParams)}
          info={{
            groupIds,
            userIds,
            packIds: packs?.map((x) => x.id),
          }}
          setIssueLicensePackIds={onIssueLicensePacksChange}
          licenseConfirmationFormRef={licenseConfirmationFormRef}
        />
      </ModalWithSteps>

      {showPaymentModal && paymentPacks && (
        <PaymentModal paymentPacks={paymentPacks} onClose={onClosePaymentModal} onPaymentSuccess={onPaymentSuccess} />
      )}
    </>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => ({
  paymentPacks: state.licensing.licenseConfirmationInfo.paymentPacks,
  isPurchaseNeeded: state.licensing?.availableLicenses?.isPurchaseNeeded,
});

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    resetContentPacks: bindAction(resetContentPacksAction, dispatch),
    updateLicensePackInfo: bindAction(updateLicensePackInfo, dispatch),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(AddPeoplePacksIssueLicenseModal);
export default ConnectedComponent;
